/**
 * The Z1 search slot (S6; docs/audits/PILOT-QUOTES-SHELL.md, S6 as built): one input in the top
 * bar, with two modes.
 *
 * Search (the default)
 *   ≥ 2 characters, 250 ms debounce, stale answers dropped (useSlotSearch). First the module
 *   registry's search providers, in registry order (G0; Quotes: number, customer, status,
 *   value — a quote opens /quotes/:id, push: list → record). Then products, customers and notes
 *   from /api/search, landing where they land today. A provider with afterGroup sits after that
 *   group instead (O6: Orders — number, customer, status, total — after Customers; slotSources). A record opened from inside its own module
 *   keeps the current list query (polish 4: recordHrefFrom); only "See all" changes the list. Footer: one row per "see all", in order —
 *   in the groups' order: Quotes' ("See all in Quotes" → /quotes?view=all&q=<term>), the
 *   customer group's (C6, §3 D10: "See all in Customers" →
 *   /customers?view=all&scope=all&q=<term>), then Orders' (O6: "See all in Orders" →
 *   /orders?view=all&q=<term>). The customers row asks for every view and scope
 *   because the slot's customer rows come from /api/search company-wide: the user's stored
 *   default view or the `my` scope would otherwise narrow what "see all" shows. The server still
 *   fences anyone who may not see all (applied: 'my'). Customers have no search provider (D10):
 *   their seeAll is this file's, not the registry's. The footer shows even when nothing is found.
 * Commands (the palette — the same slot, not a second surface)
 *   `>` as the first character, or Ctrl+K / Cmd+K, which focuses the slot with `>` typed.
 *   Entries come from the shell's providers (shellProviders.js) and the registered page's
 *   (paletteHostStore.js); the palette defines none of its own. Fuzzy match on the label,
 *   grouped; an unavailable entry is shown with its reason and Enter does nothing on it.
 *
 * Keys, all of them: `/` focuses the slot from anywhere outside an editable field; Ctrl+K /
 * Cmd+K opens the commands; in the dropdown ↑ ↓ move and Enter opens or runs; Esc clears and
 * closes — in search, focus stays in the input; in commands, it returns to where the palette
 * was opened. Neither `/` nor Ctrl+K reaches out of an open modal dialog, and on a page that
 * binds them itself (Products) they stay the page's.
 * Compact (tablet pass 2026-10-20, item 4; the header passes it through HeaderSearchSlot when it
 * has under 160 px for the slot, and always below 1,024 px since round 2, P1): a 44 px Search
 * button stands in for the box. A tap, `/` or Ctrl+K focuses the input, which is always mounted,
 * and opens the slot (each sets the open state itself; P7: never only the focus), which expands
 * across the header (onExpandedChange tells the header, which sets its other
 * controls aside). Expanded it carries a "Search in" select (All, or one group: Quotes,
 * Customers, Orders, Products) and a Close (X), both 44 px. Esc or the X collapses it and gives
 * focus to where the palette was opened from, else to the Search button; it also collapses when
 * focus leaves it with the dropdown closed.
 * Styles: SearchSlot.css (shell-search- prefix, tokens only).
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Package, Search, StickyNote, User, X } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useSidebarMode } from '../../../hooks/useSidebarMode';
import { navEntriesFor, PIN_MIN_WIDTH } from '../../Sidebar';
import useViewportWidth from '../tools/useViewportWidth';
import { useSalesPin } from '../salesPin';
import { moduleForPath, searchProviders } from '../../../modules/registry';
import useSlotSearch, { SLOT_MIN_CHARS } from './useSlotSearch';
import { groupEntries } from './paletteMatch';
import { SHELL_PROVIDERS } from './shellProviders';
import { usePaletteRegistration } from './paletteHostStore';
import './SearchSlot.css';

const MAX_PER_GROUP = 5;

/**
 * The compact slot's "Search in" choices (round 2, P1; the old mobile search's category select).
 * A choice keeps one group and its "see all" row; All keeps every group, Notes included.
 */
export const SEARCH_SCOPES = Object.freeze([
  { value: 'all', label: 'All' },
  { value: 'quotes', label: 'Quotes' },
  { value: 'customer', label: 'Customers' },
  { value: 'orders', label: 'Orders' },
  { value: 'product', label: 'Products' }
]);
const inScope = (scope, source) => scope === 'all' || (source && (source.id || source.type) === scope);
const LISTBOX_ID = 'shell-search-listbox';

/**
 * The other groups, in the order the header search showed them, and where each lands. A group's
 * seeAll (customers, C6) is a footer row after the providers'.
 */
const ENTITY_GROUPS = [
  { type: 'product', label: 'Products', Icon: Package, href: (r) => `/products/${r.id}` },
  {
    type: 'customer',
    label: 'Customers',
    Icon: User,
    href: (r) => `/customers/${r.id}`,
    seeAll: Object.freeze({
      title: 'See all in Customers',
      sub: (term) => `“${term}” in the customers list`,
      href: (term) => `/customers?view=all&scope=all&q=${encodeURIComponent(term)}`
    })
  },
  { type: 'note', label: 'Notes', Icon: StickyNote, href: (r) => `/customers/${r.customer_id}` }
];

function entityTitle(r) {
  switch (r.entity_type) {
    case 'product': return r.name || r.sku || 'Unnamed product';
    case 'customer': return r.name || 'Unnamed customer';
    case 'note': return r.customer_name || 'Customer note';
    default: return String(r.id);
  }
}

function entitySubtitle(r) {
  switch (r.entity_type) {
    case 'product': return [r.sku, r.manufacturer, r.category].filter(Boolean).join(' · ');
    case 'customer': return [r.email, r.phone, r.company].filter(Boolean).join(' · ');
    case 'note': return r.content || '';
    default: return '';
  }
}

/** The modules' search providers, in registry order (stable for this bundle's life). */
const PROVIDERS = searchProviders();

/**
 * The slot's sources in display order (O6): the providers with no afterGroup first, in registry
 * order (Quotes), then each entity group followed by the providers placed after it (Orders after
 * Customers). The groups and the footer's rows both follow it.
 */
export function slotSources(providers = PROVIDERS, entityGroups = ENTITY_GROUPS) {
  const known = new Set(entityGroups.map((g) => g.type));
  const lead = providers.filter((p) => !p.afterGroup || !known.has(p.afterGroup));
  return [
    ...lead.map((provider) => ({ provider })),
    ...entityGroups.flatMap((group) => [
      { group },
      ...providers.filter((p) => p.afterGroup === group.type).map((provider) => ({ provider }))
    ])
  ];
}
const SOURCES = slotSources();

/** The footer's rows, in the sources' order: Quotes, Customers, Orders. */
const SEE_ALLS = SOURCES
  .map(({ provider, group }) => provider || group)
  .filter((source) => source.seeAll)
  .map((source) => ({ key: `see-all-${source.id || source.type}`, seeAll: source.seeAll, source }));

/**
 * Where a chosen record row goes (polish 4, the G0 lap's finding 1). Opening from search keeps the
 * list state: when the record belongs to the module whose route root the current URL is inside,
 * the current query rides along (the record routes keep the list keys beside a record, and drop
 * any other key on their next move). From anywhere else, the bare record URL. "See all" rows do
 * not come here: they replace the list state.
 */
export function recordHrefFrom(href, { pathname, search } = {}) {
  if (!search || typeof href !== 'string' || href.includes('?')) return href;
  const here = moduleForPath(pathname);
  return here && moduleForPath(href) === here ? `${href}${search}` : href;
}

/** An input, textarea, select or contenteditable: `/` is typed there, not taken. */
function isEditable(el) {
  if (!el || !el.tagName) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag !== 'INPUT') return false;
  return !['button', 'checkbox', 'radio', 'submit', 'reset', 'range', 'color', 'file']
    .includes(String(el.type || '').toLowerCase());
}

/**
 * Pages that bind `/` and Ctrl+K themselves keep them: ProductManagement focuses its own search
 * on `/` and compares products on Ctrl+K (/products and /products/:id).
 */
export const pageOwnsShortcuts = (pathname) => /^\/products(\/(?!detail(\/|$))[^/]+)?\/?$/.test(pathname || '');

/** Inside an open modal dialog the keys stay there: the slot is behind it (S1b's rule too). */
const inModal = (el) => !!(el && el.closest && el.closest('[aria-modal="true"], [role="dialog"], [role="alertdialog"]'));

export default function SearchSlot({ compact = false, onExpandedChange }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin, canAccessMarketplace, permissions } = useAuth();
  const sidebar = useSidebarMode();
  const salesPin = useSalesPin();
  const registration = usePaletteRegistration();
  const viewport = useViewportWidth();

  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  // -1: the first available row; otherwise the row the arrows or the pointer chose.
  const [active, setActive] = useState(-1);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  // Where focus was when the slot was taken by `/` or Ctrl+K: Esc in commands returns there.
  const returnTargetRef = useRef(null);
  // Compact: the slot is expanded while focus is inside it or its dropdown is open.
  const [focusWithin, setFocusWithin] = useState(false);
  const [scope, setScope] = useState('all');
  const expandRef = useRef(null);
  // Set when Esc or the X collapses the slot: focus goes to the Search button once it is back.
  const refocusExpandRef = useRef(false);

  const command = term.startsWith('>');
  const query = command ? term.slice(1) : term;
  const trimmed = term.trim();
  const search = useSlotSearch(command ? '' : term, PROVIDERS);

  const navEntries = useMemo(
    () => navEntriesFor({ isAdmin, canAccessMarketplace, role: user?.role, permissions: permissions || [] }),
    [isAdmin, canAccessMarketplace, user?.role, permissions]
  );

  const showDropdown = open && (command || trimmed.length >= SLOT_MIN_CHARS);

  // ── the rows, one flat list for the arrows, grouped for display ───────────────
  let sections = [];
  if (showDropdown && command) {
    const shellCtx = {
      navEntries,
      navigate,
      pathname: location.pathname,
      sidebar: { mode: sidebar.mode, toggle: sidebar.toggle },
      pin: {
        canPin: sidebar.isRail && viewport >= PIN_MIN_WIDTH,
        pinned: salesPin.pinned,
        minWidth: PIN_MIN_WIDTH,
        setPinned: salesPin.setPinned
      }
    };
    const entries = [
      ...(registration ? registration.providers.flatMap((p) => p(registration.ctx)) : []),
      ...SHELL_PROVIDERS.flatMap((p) => p(shellCtx))
    ];
    sections = groupEntries(entries, query).map((g) => ({
      key: g.group,
      label: g.group,
      rows: g.entries.map((entry) => ({ key: entry.id, kind: 'command', entry, available: entry.available === true }))
    }));
  } else if (showDropdown) {
    const fresh = search.term === trimmed;
    if (fresh) {
      SOURCES.forEach(({ provider, group: g }) => {
        if (compact && !inScope(scope, provider || g)) return;
        if (provider) {
          const rows = search.groups[provider.id] || [];
          if (rows.length) {
            sections.push({
              key: provider.id,
              label: provider.label,
              rows: rows.map((r) => ({ key: provider.rowKey(r), kind: provider.kind, provider, result: r, available: true }))
            });
          }
          return;
        }
        const rows = search.others.filter((r) => r.entity_type === g.type).slice(0, MAX_PER_GROUP);
        if (rows.length) {
          sections.push({
            key: g.type,
            label: g.label,
            rows: rows.map((r) => ({ key: `${g.type}-${r.id}`, kind: 'entity', result: r, group: g, available: true }))
          });
        }
      });
    }
    const seeAlls = compact ? SEE_ALLS.filter((s) => inScope(scope, s.source)) : SEE_ALLS;
    if (seeAlls.length) {
      sections.push({
        key: 'footer',
        label: null,
        rows: seeAlls.map(({ key, seeAll }) => ({ key, kind: 'seeAll', seeAll, available: true }))
      });
    }
  }
  const flat = sections.flatMap((s) => s.rows);
  const firstAvailable = Math.max(0, flat.findIndex((r) => r.available));
  const activeIndex = flat.length ? (active < 0 ? firstAvailable : Math.min(active, flat.length - 1)) : -1;
  const optionId = (i) => `shell-search-opt-${i}`;

  // ── open / close ────────────────────────────────────────────────────────────
  const finish = useCallback(() => {
    setTerm('');
    setOpen(false);
    setActive(-1);
  }, []);

  /** Close; in commands, focus goes back to where the palette was opened. */
  const closeAndReturn = useCallback((toTarget) => {
    const target = returnTargetRef.current;
    returnTargetRef.current = null;
    finish();
    if (toTarget && target && target !== inputRef.current && target.isConnected && typeof target.focus === 'function') {
      target.focus();
    }
  }, [finish]);

  const activate = (row) => {
    if (!row || !row.available) return;
    if (row.kind === 'command') {
      const target = returnTargetRef.current;
      returnTargetRef.current = null;
      finish();
      row.entry.run({ returnTarget: target && target !== inputRef.current ? target : null });
      // A command that moved focus (a tool's heading, say) keeps it; otherwise back to the page.
      if (document.activeElement === inputRef.current) {
        if (target && target !== inputRef.current && target.isConnected) target.focus();
        else inputRef.current.blur();
      }
      return;
    }
    returnTargetRef.current = null;
    finish();
    // A result chosen is a navigation: the slot lets go, so the page it opens can take focus
    // (a quote's record does; QuoteSplit never takes it from a field that still holds it).
    if (inputRef.current) inputRef.current.blur();
    if (row.provider) navigate(recordHrefFrom(row.provider.href(row.result), location));
    else if (row.kind === 'entity') navigate(recordHrefFrom(row.group.href(row.result), location));
    else if (row.kind === 'seeAll') navigate(row.seeAll.href(trimmed));
  };

  /**
   * Compact: close and collapse. Focus goes back to where `/` or Ctrl+K took it from (outside the
   * slot), else to the Search button.
   */
  const collapse = () => {
    const target = returnTargetRef.current;
    returnTargetRef.current = null;
    finish();
    setFocusWithin(false);
    const usable = target && target !== document.body && target.isConnected && typeof target.focus === 'function'
      && !(wrapRef.current && wrapRef.current.contains(target));
    if (usable) {
      target.focus();
    } else {
      refocusExpandRef.current = true;
      if (wrapRef.current && wrapRef.current.contains(document.activeElement)) document.activeElement.blur();
    }
  };

  /**
   * Open the slot: focus the field and set the open state. The state is set here, not left to the
   * field's focus event: a window without focus (P7, the 2026-10-21 lap: DevTools' device toolbar)
   * moves activeElement on focus() but fires no focus event, so a slot opened by that event alone
   * took focus and stayed shut. `/` and the Search button (tap, click, Enter, Space) both come here.
   */
  const openSlot = useCallback((returnTarget) => {
    returnTargetRef.current = returnTarget;
    if (inputRef.current) inputRef.current.focus();
    setOpen(true);
  }, []);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (compact) collapse();
      else closeAndReturn(command);
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!showDropdown) {
        if (term) setOpen(true);
        return;
      }
      if (!flat.length) return;
      const delta = e.key === 'ArrowDown' ? 1 : -1;
      setActive(Math.min(flat.length - 1, Math.max(0, activeIndex + delta)));
      return;
    }
    if (e.key === 'Enter' && showDropdown && activeIndex >= 0) {
      e.preventDefault();
      activate(flat[activeIndex]);
    }
  };

  // ── the two shortcuts, document-wide ──────────────────────────────────────────
  const stateRef = useRef({});
  stateRef.current = { pathname: location.pathname, open, command };
  useEffect(() => {
    const onKey = (e) => {
      if (e.defaultPrevented || !inputRef.current) return;
      const { pathname, open: isOpen, command: inCommands } = stateRef.current;
      const mod = (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && (e.key === 'k' || e.key === 'K');
      if (mod) {
        if (pageOwnsShortcuts(pathname) || inModal(e.target)) return;
        e.preventDefault();
        const inSlot = wrapRef.current && wrapRef.current.contains(document.activeElement);
        if (inSlot && isOpen && inCommands) {
          closeAndReturn(true);
          return;
        }
        if (!inSlot) returnTargetRef.current = document.activeElement;
        setTerm('>');
        setActive(-1);
        setOpen(true);
        inputRef.current.focus();
        return;
      }
      if (e.key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        if (pageOwnsShortcuts(pathname) || isEditable(e.target) || inModal(e.target)) return;
        e.preventDefault();
        openSlot(document.activeElement);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [closeAndReturn, openSlot]);

  // A click outside closes the dropdown; what was typed stays.
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('touchstart', away);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('touchstart', away);
    };
  }, [open]);

  // Keep the chosen row in view.
  useEffect(() => {
    if (activeIndex < 0) return;
    const el = document.getElementById(optionId(activeIndex));
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  // ── render ─────────────────────────────────────────────────────────────────
  let index = -1;
  const renderRow = (row) => {
    index += 1;
    const i = index;
    const selected = i === activeIndex;
    const common = {
      id: optionId(i),
      role: 'option',
      'aria-selected': selected,
      'aria-disabled': row.available ? undefined : true,
      'data-kind': row.kind,
      className: `shell-search-option${selected ? ' is-active' : ''}`,
      onMouseDown: (e) => e.preventDefault(), // keep focus in the input
      onMouseEnter: () => setActive(i),
      onClick: () => activate(row)
    };
    if (row.provider) {
      return (
        <div key={row.key} {...common}>
          {row.provider.renderRow(row.result)}
        </div>
      );
    }
    if (row.kind === 'entity') {
      const { Icon } = row.group;
      return (
        <div key={row.key} {...common}>
          <Icon className="shell-search-icon" aria-hidden="true" />
          <span className="shell-search-main">
            <span className="shell-search-title">{entityTitle(row.result)}</span>
            <span className="shell-search-sub">{entitySubtitle(row.result)}</span>
          </span>
        </div>
      );
    }
    if (row.kind === 'seeAll') {
      return (
        <div key={row.key} {...common} className={`${common.className} shell-search-footer-row`}>
          <Search className="shell-search-icon" aria-hidden="true" />
          <span className="shell-search-main">
            <span className="shell-search-title">{row.seeAll.title}</span>
            <span className="shell-search-sub">{row.seeAll.sub(trimmed)}</span>
          </span>
        </div>
      );
    }
    const { entry } = row;
    return (
      <div key={row.key} {...common}>
        <span className="shell-search-main">
          <span className="shell-search-title">{entry.label}</span>
          {!row.available && <span className="shell-search-reason">{entry.available}</span>}
        </span>
        {entry.hint ? <span className="shell-search-hint">{entry.hint}</span> : null}
      </div>
    );
  };

  const searching = !command && search.loading;
  const fresh = search.term === trimmed;
  const providerErrors = PROVIDERS.map((p) => search.errors[p.id]).filter(Boolean);
  const providerRows = PROVIDERS.some((p) => (search.groups[p.id] || []).length > 0);
  const nothingFound = !command && fresh && !search.loading && !providerRows && !search.others.length
    && !providerErrors.length && !search.othersError;

  const expanded = compact && (focusWithin || open);
  const slotClass = ['shell-search', compact && 'is-compact', expanded && 'is-expanded'].filter(Boolean).join(' ');

  // The header sets its other controls aside while the slot is expanded across it.
  const expandedChangeRef = useRef(onExpandedChange);
  expandedChangeRef.current = onExpandedChange;
  const reportedRef = useRef(false);
  // Before paint, so the bar never shows the slot over controls that have not stepped aside.
  useLayoutEffect(() => {
    if (reportedRef.current === expanded) return;
    reportedRef.current = expanded;
    if (expandedChangeRef.current) expandedChangeRef.current(expanded);
  }, [expanded]);

  // Back from Esc or the X: the Search button has focus.
  useEffect(() => {
    if (expanded || !refocusExpandRef.current) return;
    refocusExpandRef.current = false;
    if (expandRef.current) expandRef.current.focus();
  }, [expanded]);

  return (
    <div
      className={slotClass}
      ref={wrapRef}
      onFocus={(e) => { if (e.target !== expandRef.current) setFocusWithin(true); }}
      onBlur={(e) => {
        if (wrapRef.current && wrapRef.current.contains(e.relatedTarget)) return;
        setFocusWithin(false);
        // Compact, opened by the button or `/` with the dropdown closed: focus leaving collapses it.
        if (compact && !showDropdown) setOpen(false);
      }}
    >
      {compact && !expanded && (
        <button
          ref={expandRef}
          type="button"
          className="shell-search-expand"
          aria-label="Search"
          title="Search (Ctrl K)"
          onClick={() => openSlot(null)}
        >
          <Search aria-hidden="true" />
        </button>
      )}
      <div className={`shell-search-box${open ? ' is-open' : ''}`}>
        <Search className="shell-search-box-icon" aria-hidden="true" />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          className="shell-search-input"
          aria-label="Search, or type > for commands"
          aria-expanded={showDropdown}
          aria-controls={LISTBOX_ID}
          aria-autocomplete="list"
          aria-activedescendant={showDropdown && activeIndex >= 0 ? optionId(activeIndex) : undefined}
          autoComplete="off"
          spellCheck={false}
          placeholder="Search quotes, customers, products…  (> for commands)"
          value={term}
          onChange={(e) => { setTerm(e.target.value); setActive(-1); setOpen(true); }}
          onFocus={() => { if (term) setOpen(true); }}
          onKeyDown={onKeyDown}
        />
        {!term && !expanded && <kbd className="shell-search-kbd" aria-hidden="true">Ctrl K</kbd>}
        {expanded && (
          <>
            <select
              className="shell-search-scope"
              aria-label="Search in"
              value={scope}
              onChange={(e) => {
                setScope(e.target.value);
                setActive(-1);
                if (inputRef.current) inputRef.current.focus();
              }}
            >
              {SEARCH_SCOPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <button type="button" className="shell-search-close" aria-label="Close search" onClick={collapse}>
              <X aria-hidden="true" />
            </button>
          </>
        )}
      </div>

      {showDropdown && (
        <div className="shell-search-pop">
          <div role="listbox" id={LISTBOX_ID} aria-label={command ? 'Commands' : 'Search results'}>
            {sections.map((section) => (section.label ? (
              <div key={section.key} role="group" aria-label={section.label} className="shell-search-group">
                <div className="shell-search-heading" aria-hidden="true">{section.label}</div>
                {section.rows.map(renderRow)}
              </div>
            ) : (
              <div key={section.key} role="presentation" className="shell-search-group shell-search-footer">
                {section.rows.map(renderRow)}
              </div>
            )))}
          </div>
          {searching && <div className="shell-search-note" role="status">Searching…</div>}
          {!command && fresh && providerErrors.map((text) => <div key={text} className="shell-search-note">{text}</div>)}
          {!command && fresh && search.othersError && <div className="shell-search-note">{search.othersError}</div>}
          {nothingFound && <div className="shell-search-note">{`No results for “${trimmed}”`}</div>}
          {command && !flat.length && <div className="shell-search-note">{`No commands match “${query.trim()}”`}</div>}
          <div className="shell-search-keys" aria-hidden="true">
            <span><kbd>↑</kbd><kbd>↓</kbd> move</span>
            <span><kbd>↵</kbd> {command ? 'run' : 'open'}</span>
            <span><kbd>Esc</kbd> close</span>
            {!command && <span><kbd>&gt;</kbd> commands</span>}
          </div>
        </div>
      )}
    </div>
  );
}
